import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, BookOpen, Search, X, Calculator as CalcIcon, CalendarDays, Sparkles, Star, ArrowUp, ZoomIn, GraduationCap, ShoppingBag } from 'lucide-react';
import { Input } from '../components/ui/input';
import { DosageDiary } from '../components/DosageDiary';
import { LibraryConcierge } from '../components/LibraryConcierge';
import { libraryVerify, libraryGetItems, libraryGetDeals, getProducts } from '../lib/api';
import { useCart } from '../context/CartContext';

const TOKEN_KEY = 'alp_library_token';
const FAVS_KEY = 'alp_library_favs';

// One-line "best known for" summary pulled from the guide's use list.
const bestFor = (item) => {
  const first = (item.use || '').split('\n').map((s) => s.trim()).filter(Boolean)[0];
  return first || item.category || '';
};

const BEGINNER_TITLES = ['How to reconstitute', 'Simple Peptide Beginner Bible', 'The Peptide Education Guide'];

// Match a guide to shop products by name (longest names win so combos outrank their parts).
const normalize = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const PRODUCT_ALIASES = { 'GLOW Blend': 'glow', 'KLOW Blend': 'klow', 'Melanotan II': 'mt2' };
const matchProducts = (item, products) => {
  const t = normalize(item.title);
  if (!t) return [];
  const hitOn = (p) => {
    const n = normalize(p.name);
    if (n.length >= 3 && t.includes(n)) return true;
    const alias = PRODUCT_ALIASES[p.name];
    return alias ? t.includes(alias) : false;
  };
  let hits = products.filter(hitOn);
  hits = hits.filter((p) => !hits.some((o) => o !== p && normalize(o.name).includes(normalize(p.name)) && normalize(o.name).length > normalize(p.name).length));
  return hits.sort((a, b) => normalize(b.name).length - normalize(a.name).length).slice(0, 2);
};

// Build a /calculator link prefilled from a library entry (parses vial mg + BAC ml + starting dose).
const calcLinkFor = (item) => {
  const mgMatch = `${item.title} ${item.body || ''}`.match(/(\d+(?:\.\d+)?)\s*mg/i);
  const mlMatch = (item.bacWater || '').match(/(\d+(?:\.\d+)?)\s*ml/i);
  const doseMatch = (item.dosage || '').match(/(\d+(?:\.\d+)?)\s*(mg|mcg)/i);
  const params = new URLSearchParams();
  if (item.title) params.set('name', item.title);
  if (mgMatch) params.set('peptideMg', mgMatch[1]);
  if (mlMatch) params.set('waterMl', mlMatch[1]);
  if (doseMatch) {
    const val = parseFloat(doseMatch[1]);
    const mg = doseMatch[2].toLowerCase() === 'mcg' ? val / 1000 : val;
    params.set('targetMg', String(mg));
  }
  return `/calculator?${params.toString()}`;
};

export default function Library() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [unlocked, setUnlocked] = useState(false);
  const [items, setItems] = useState([]);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('alp_library_email') || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [obscured, setObscured] = useState(false);
  const [activeCat, setActiveCat] = useState('All');
  const [openItem, setOpenItem] = useState(null);
  const [deals, setDeals] = useState([]);
  const [copiedCode, setCopiedCode] = useState('');
  const [showDiary, setShowDiary] = useState(false);
  const [showConcierge, setShowConcierge] = useState(false);
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAVS_KEY) || '[]'); } catch (e) { return []; }
  });
  const [letter, setLetter] = useState('');
  const [viewerSrc, setViewerSrc] = useState('');
  const [viewerZoom, setViewerZoom] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [stickyTop, setStickyTop] = useState(0);
  const [products, setProducts] = useState([]);
  const { addToCart, openDrawer } = useCart();

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id];
      try { localStorage.setItem(FAVS_KEY, JSON.stringify(next)); } catch (e) {}
      return next;
    });
  };

  const loadItems = async (tok) => {
    try {
      const data = await libraryGetItems(tok);
      setItems(data);
      setUnlocked(true);
      libraryGetDeals(tok).then(setDeals).catch(() => setDeals([]));
      getProducts().then(setProducts).catch(() => setProducts([]));
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken('');
      setUnlocked(false);
    }
  };

  const dealLabel = (d) => (d.type === 'percentage' ? `${d.amount}% OFF` : `$${Number(d.amount).toFixed(2)} OFF`);
  const copyCode = (code) => {
    try { navigator.clipboard.writeText(code); } catch (e) {}
    setCopiedCode(code);
    setTimeout(() => setCopiedCode((c) => (c === code ? '' : c)), 1500);
  };

  useEffect(() => {
    if (token) loadItems(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track scroll (compact sticky search + back-to-top) and measure header height for the sticky offset.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 420);
    const measure = () => setStickyTop(document.querySelector('header')?.offsetHeight || 0);
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', measure); };
  }, []);

  // Content-protection deterrents (active only while unlocked).
  useEffect(() => {
    if (!unlocked) return undefined;
    const block = (e) => { e.preventDefault(); return false; };
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      // Block save / print / copy / select-all / view-source
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'c', 'u', 'a'].includes(k)) { e.preventDefault(); return false; }
      // PrintScreen: wipe clipboard as a deterrent
      if (k === 'printscreen') {
        try { navigator.clipboard.writeText(''); } catch (_) { /* noop */ }
        setObscured(true);
        setTimeout(() => setObscured(false), 1500);
      }
      return undefined;
    };
    const onBlur = () => setObscured(true);
    const onFocus = () => setObscured(false);
    const onVis = () => setObscured(document.hidden);
    document.addEventListener('contextmenu', block);
    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('dragstart', block);
    document.addEventListener('keydown', onKey);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('dragstart', block);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [unlocked]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await libraryVerify(email, password);
      localStorage.setItem(TOKEN_KEY, res.token);
      localStorage.setItem('alp_library_email', email);
      setToken(res.token);
      await loadItems(res.token);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const lock = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(''); setUnlocked(false); setItems([]); setDeals([]); setPassword('');
  };

  if (!unlocked) {
    return (
      <div className="bg-alp-black min-h-screen flex items-center justify-center px-5">
        <form onSubmit={submit} className="w-full max-w-md text-center" data-testid="library-gate-form">
          <div className="w-16 h-16 rounded-full border border-alp-gold/40 flex items-center justify-center mx-auto mb-6">
            <Lock size={26} className="text-alp-gold" />
          </div>
          <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">Client Access</p>
          <h1 className="font-serif text-4xl text-alp-cream mb-3">Help Library</h1>
          <p className="text-alp-gray text-sm mb-8">Enter your email and access password to view the client help library.</p>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email"
            data-testid="library-email-input"
            className="bg-alp-card/50 border-alp-line text-alp-cream text-center h-12 mb-3"
            autoFocus
          />
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Access password"
            data-testid="library-password-input"
            className="bg-alp-card/50 border-alp-line text-alp-cream text-center h-12"
          />
          {error && <p className="text-red-400 text-sm mt-3" data-testid="library-error">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password || !email}
            data-testid="library-unlock-btn"
            className="mt-6 w-full bg-alp-gold text-alp-black py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-50"
          >
            {loading ? 'Unlocking…' : 'Unlock Library'}
          </button>
        </form>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const hay = (i) => [i.title, i.body, i.category, i.whatItIs, i.use, i.dosage, i.timing, i.cycle, i.bacWater].filter(Boolean).join(' ').toLowerCase();
  const searched = q ? items.filter((i) => q.split(/\s+/).every((w) => hay(i).includes(w))) : items;
  const lettered = letter ? searched.filter((i) => (i.title || '').trim().toUpperCase().startsWith(letter)) : searched;
  const CAT_ORDER = ['Getting Started', 'Recovery & Repair', 'Skin & Cosmetic', 'Metabolic & Growth', 'Wellness & Cognitive'];
  const sortCats = (arr) => [...arr].sort((a, b) => {
    const ia = CAT_ORDER.indexOf(a); const ib = CAT_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const allCats = sortCats([...new Set(items.map((i) => i.category || 'General'))]);
  // While searching or letter-jumping, look across ALL categories so nothing is hidden by the active tab.
  const visible = q || letter || activeCat === 'All' ? lettered : lettered.filter((i) => (i.category || 'General') === activeCat);
  const visibleCats = sortCats([...new Set(visible.map((i) => i.category || 'General'))]);
  const letters = [...new Set(items.map((i) => (i.title || '').trim().charAt(0).toUpperCase()).filter((c) => /[A-Z0-9]/.test(c)))].sort();
  const favItems = items.filter((i) => favs.includes(i.id));
  const beginnerItems = BEGINNER_TITLES.map((t) => items.find((i) => (i.title || '').toLowerCase() === t.toLowerCase())).filter(Boolean);

  const GuideCard = (item) => (
    <div key={item.id} className="relative group">
      <button
        onClick={() => setOpenItem(item)}
        data-testid={`library-card-${item.id}`}
        className="w-full h-full text-left border border-alp-line rounded-lg bg-alp-card/40 hover:border-alp-gold/60 hover:bg-alp-card/70 transition-colors overflow-hidden flex flex-col"
      >
        {item.image ? (
          <img src={item.image} alt={item.title} draggable={false} onContextMenu={(e) => e.preventDefault()} className="w-full h-32 object-cover pointer-events-none select-none" />
        ) : null}
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-serif text-lg text-alp-cream group-hover:text-alp-gold transition-colors mb-1 pr-8">{item.title}</h3>
          {bestFor(item) ? (
            <p className="text-alp-cream/60 text-xs leading-relaxed mb-2" data-testid={`library-bestfor-${item.id}`}>Best known for: {bestFor(item)}</p>
          ) : null}
          {(item.dosage || item.timing) ? (
            <p className="text-alp-gold text-xs leading-relaxed">{[item.dosage, item.timing].filter(Boolean).join(' \u00b7 ')}</p>
          ) : (
            <p className="text-alp-gray text-xs">Reference guide</p>
          )}
          <p className="text-alp-gray text-[10px] mt-4 tracking-[0.15em] uppercase group-hover:text-alp-gold transition-colors">View guide &rarr;</p>
        </div>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); toggleFav(item.id); }}
        data-testid={`library-fav-${item.id}`}
        aria-label={favs.includes(item.id) ? 'Remove from My Guides' : 'Add to My Guides'}
        className={`absolute top-2 right-2 z-10 w-9 h-9 rounded-full flex items-center justify-center border transition-colors backdrop-blur-sm ${favs.includes(item.id) ? 'bg-alp-gold/20 border-alp-gold text-alp-gold' : 'bg-alp-black/60 border-alp-line text-alp-gray hover:text-alp-gold hover:border-alp-gold/60'}`}
      >
        <Star size={16} fill={favs.includes(item.id) ? 'currentColor' : 'none'} />
      </button>
    </div>
  );

  return (
    <div className="bg-alp-black min-h-screen relative" style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}>
      <style>{`@media print { body { display: none !important; } }`}</style>
      {/* Traceable watermark (email tiled) — deters sharing of screenshots */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-40 overflow-hidden opacity-[0.06]" data-testid="library-watermark" style={{ transform: 'rotate(-30deg)' }}>
        <div className="whitespace-nowrap text-alp-cream text-sm leading-[3.5rem]">
          {Array.from({ length: 40 }).map((_, i) => (
            <div key={i}>{Array.from({ length: 14 }).map((__, j) => (<span key={j} className="mx-6">AUSTRALIANLUXEPEPTIDES</span>))}</div>
          ))}
        </div>
      </div>

      {/* Obscure content when the window loses focus / on PrintScreen */}
      {obscured && (
        <div className="fixed inset-0 z-[60] bg-alp-black flex items-center justify-center" data-testid="library-obscured">
          <p className="text-alp-gold text-sm tracking-[0.2em] uppercase">Content hidden</p>
        </div>
      )}
      <div className="border-b border-alp-line">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-14 text-center">
          <div className="inline-flex items-center gap-2 text-alp-gold text-xs tracking-[0.3em] uppercase mb-3"><BookOpen size={15} /> Client Help Library</div>
          <h1 className="font-serif text-4xl md:text-5xl text-alp-cream heading-glow">How-To Guides</h1>
          <div className="max-w-md mx-auto mt-8 relative">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-alp-gray" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides, compounds, dosages..."
              data-testid="library-search"
              className="w-full bg-alp-card/50 border border-alp-line rounded-full pl-11 pr-10 py-3 text-sm text-alp-cream placeholder-alp-gray focus:outline-none focus:border-alp-gold transition-colors"
            />
            {query ? (
              <button onClick={() => setQuery('')} data-testid="library-search-clear" aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-alp-gray hover:text-alp-gold transition-colors">
                <X size={15} />
              </button>
            ) : null}
          </div>
          {q ? (
            <p className="text-alp-gray text-xs mt-3" data-testid="library-search-count">
              {visible.length === 0 ? 'No guides match' : `${visible.length} guide${visible.length === 1 ? '' : 's'} match`} &ldquo;{query.trim()}&rdquo; across all categories
            </p>
          ) : null}
          <div className="mt-5 flex items-center justify-center gap-5">
            <button onClick={() => setShowDiary((v) => !v)} data-testid="diary-toggle-btn" className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-alp-gold hover:text-alp-goldlight transition-colors">
              <CalendarDays size={14} /> {showDiary ? 'Hide Diary' : 'Weekly Dosage Diary'}
            </button>
            <button onClick={() => setShowConcierge((v) => !v)} data-testid="concierge-toggle-btn" className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-alp-gold hover:text-alp-goldlight transition-colors">
              <Sparkles size={14} /> {showConcierge ? 'Hide Concierge' : 'Ask the Guides'}
            </button>
            <button onClick={lock} data-testid="library-lock-btn" className="text-xs tracking-[0.15em] uppercase text-alp-gray hover:text-alp-gold transition-colors">Lock library</button>
          </div>
        </div>
      </div>

      {showConcierge ? (
        <div className="border-b border-alp-line" data-testid="library-concierge-section">
          <div className="max-w-3xl mx-auto px-5 md:px-8 py-12">
            <LibraryConcierge />
          </div>
        </div>
      ) : null}

      {showDiary ? (
        <div className="border-b border-alp-line" data-testid="library-diary-section">
          <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
            <DosageDiary items={items} />
          </div>
        </div>
      ) : null}

      {/* VIP Deals (library members only) */}
      {deals.length > 0 ? (
        <div className="border-b border-alp-line bg-gradient-to-b from-alp-gold/[0.06] to-transparent" data-testid="library-vip-deals">
          <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
            <div className="text-center mb-8">
              <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-2">Members Only</p>
              <h2 className="font-serif text-3xl md:text-4xl text-alp-cream heading-glow">VIP Deals &amp; Specials</h2>
              <p className="text-alp-gray text-sm mt-2">Exclusive discount codes for library members. Apply at checkout.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {deals.map((d) => (
                <div key={d.id} data-testid={`vip-deal-${d.code}`} className="border border-alp-gold/40 rounded-lg bg-alp-card/60 p-6 flex flex-col">
                  <p className="font-serif text-3xl text-alp-gold">{dealLabel(d)}</p>
                  {d.description ? <p className="text-alp-cream/85 text-sm mt-2 leading-relaxed">{d.description}</p> : null}
                  <p className="text-alp-gray text-xs mt-3">
                    {d.minOrder ? `Min. order $${Number(d.minOrder).toFixed(2)}` : 'No minimum'}
                    {d.expiry ? ` \u00b7 expires ${d.expiry}` : ''}
                  </p>
                  <div className="mt-auto pt-5">
                    <button
                      onClick={() => copyCode(d.code)}
                      data-testid={`vip-copy-${d.code}`}
                      className="btn-glow w-full inline-flex items-center justify-center gap-2 border border-dashed border-alp-gold/60 text-alp-cream px-4 py-3 rounded text-sm tracking-[0.15em] uppercase hover:border-alp-gold hover:text-alp-gold transition-colors"
                    >
                      {copiedCode === d.code ? 'Copied!' : `Code: ${d.code}`}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Beginner's path */}
      {beginnerItems.length > 0 ? (
        <div className="border-b border-alp-line" data-testid="library-beginner-path">
          <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
            <div className="flex items-center gap-2 justify-center mb-4 text-alp-gold text-xs tracking-[0.25em] uppercase">
              <GraduationCap size={15} /> New here? Start with these
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              {beginnerItems.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => setOpenItem(item)}
                  data-testid={`library-beginner-${i}`}
                  className="flex items-center gap-3 border border-alp-gold/30 rounded-lg bg-alp-card/40 px-4 py-3 text-left hover:border-alp-gold hover:bg-alp-card/70 transition-colors"
                >
                  <span className="w-7 h-7 shrink-0 rounded-full border border-alp-gold/50 text-alp-gold text-xs flex items-center justify-center">{i + 1}</span>
                  <span className="text-alp-cream text-sm font-serif">{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Sticky toolbar: compact search (after scrolling) + category tabs + A–Z jump */}
      {allCats.length > 0 ? (
        <div className="sticky z-30 border-b border-alp-line bg-alp-black/90 backdrop-blur-md" style={{ top: stickyTop }} data-testid="library-category-tabs">
          <div className="max-w-5xl mx-auto px-5 md:px-8 py-3">
            {scrolled ? (
              <div className="relative max-w-sm mx-auto mb-3" data-testid="library-sticky-search">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-alp-gray" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search guides..."
                  className="w-full bg-alp-card/60 border border-alp-line rounded-full pl-9 pr-8 py-2 text-xs text-alp-cream placeholder-alp-gray focus:outline-none focus:border-alp-gold transition-colors"
                />
                {query ? (
                  <button onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center text-alp-gray hover:text-alp-gold">
                    <X size={13} />
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="flex gap-2 overflow-x-auto md:flex-wrap md:justify-center pb-1 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {['All', ...allCats].map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveCat(cat); setLetter(''); }}
                  data-testid={`library-cat-${cat.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  className={`shrink-0 px-4 py-2 rounded-full text-xs tracking-[0.12em] uppercase transition-colors border ${activeCat === cat && !letter ? 'bg-alp-gold text-alp-black border-alp-gold' : 'border-alp-line text-alp-gray hover:text-alp-gold hover:border-alp-gold/60'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            {letters.length > 1 ? (
              <div className="flex gap-1 mt-2 overflow-x-auto md:flex-wrap md:justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="library-az-strip">
                {letters.map((L) => (
                  <button
                    key={L}
                    onClick={() => { setLetter((cur) => (cur === L ? '' : L)); setActiveCat('All'); }}
                    data-testid={`library-letter-${L}`}
                    className={`shrink-0 w-7 h-7 rounded text-[11px] font-medium transition-colors ${letter === L ? 'bg-alp-gold text-alp-black' : 'text-alp-gray hover:text-alp-gold hover:bg-alp-card/70'}`}
                  >
                    {L}
                  </button>
                ))}
                {letter ? (
                  <button onClick={() => setLetter('')} data-testid="library-letter-clear" className="shrink-0 h-7 px-2 rounded text-[11px] text-alp-gold hover:text-alp-goldlight inline-flex items-center gap-1">
                    <X size={11} /> Clear
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        {/* My Guides (favourites) */}
        {favItems.length > 0 && !q && !letter && activeCat === 'All' ? (
          <div className="mb-12" data-testid="library-my-guides">
            <h2 className="font-serif text-2xl text-alp-gold mb-6 border-b border-alp-line pb-3 inline-flex items-center gap-2 w-full"><Star size={18} fill="currentColor" /> My Guides</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {favItems.map((item) => GuideCard(item))}
            </div>
          </div>
        ) : null}

        {visible.length === 0 ? (
          <p className="text-center text-alp-gray py-20" data-testid="library-empty">{items.length === 0 ? 'No guides available yet.' : 'No guides match your search.'}</p>
        ) : (
          visibleCats.map((cat) => (
            <div key={cat} className="mb-12">
              <h2 className="font-serif text-2xl text-alp-gold mb-6 border-b border-alp-line pb-3">{cat}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {visible.filter((i) => (i.category || 'General') === cat).map((item) => GuideCard(item))}
              </div>
            </div>
          ))
        )}

        {/* COPYRIGHT & EDUCATIONAL DISCLAIMER */}
        <div className="mt-20 border-t border-alp-line pt-10" data-testid="library-disclaimer">
          <details className="group">
            <summary className="cursor-pointer list-none flex items-center justify-center gap-2 text-alp-gold text-xs tracking-[0.2em] uppercase select-none">
              Copyright &amp; Educational Disclaimer
              <span className="text-alp-gray group-open:rotate-180 transition-transform">&#9662;</span>
            </summary>
            <div className="max-w-3xl mx-auto mt-8 space-y-6 text-alp-gray text-[13px] leading-relaxed">
              <p className="text-alp-cream tracking-[0.15em] uppercase text-xs">Australian Luxe</p>
              <div>
                <p className="text-alp-cream font-medium mb-1">Copyright &amp; Educational Disclaimer</p>
                <p>Copyright &copy; Australian Luxe, 2026. All rights reserved.</p>
                <p>All Australian Luxe content, guides, graphics, product library materials, education resources, captions and associated documents are the intellectual property of Australian Luxe and may not be copied, reproduced, edited, screenshotted for reuse, redistributed, republished, or used for commercial purposes without written permission.</p>
                <p>Unauthorised reproduction, resale, editing, watermark removal, or redistribution of Australian Luxe materials is strictly prohibited.</p>
              </div>
              <div>
                <p className="text-alp-cream font-medium mb-1">Not Medical Advice</p>
                <p>The information provided by Australian Luxe is for general informational, educational and research-reference purposes only. It is not medical advice, healthcare advice, diagnostic advice, treatment advice, or a substitute for guidance from a qualified healthcare professional. Nothing provided by Australian Luxe creates a doctor-patient, practitioner-client, or healthcare provider relationship. No information provided is intended to diagnose, treat, cure, prevent, or manage any disease, condition, injury, symptom, or health concern.</p>
              </div>
              <div>
                <p className="text-alp-cream font-medium mb-1">Research Use Only</p>
                <p>All products and educational materials referenced by Australian Luxe are intended for research and educational reference only. Products are not intended for human consumption. Any reference to a product, compound, amount, preparation method, timing, cycle, category, pathway, or protocol is provided for research discussion only and does not constitute a recommendation, prescription, instruction, endorsement, or encouragement to use.</p>
              </div>
              <div>
                <p className="text-alp-cream font-medium mb-1">Professional Guidance</p>
                <p>Always seek advice from a licensed medical professional or qualified healthcare provider before making decisions relating to a medical condition, medication, supplement, peptide, compound, treatment, protocol, or health-related concern. Never disregard, delay, or avoid seeking professional medical advice because of something you have read, received, or viewed from Australian Luxe.</p>
              </div>
              <div>
                <p className="text-alp-cream font-medium mb-1">Legal &amp; Regulatory Responsibility</p>
                <p>Some substances discussed may be regulated, restricted, prescription-only, not approved for human use, or subject to different legal requirements depending on your location. You are solely responsible for understanding and complying with all applicable laws, regulations, import rules, storage requirements, and use restrictions in your jurisdiction. Australian Luxe does not provide legal advice.</p>
              </div>
              <div>
                <p className="text-alp-cream font-medium mb-1">Individual Results &amp; Risk</p>
                <p>Individual responses may vary. Any examples, anecdotes, testimonials, or shared experiences are personal experiences only and are not guarantees of outcomes. Results may not be typical. Use of any information, product, or research material is entirely at your own risk.</p>
              </div>
              <div>
                <p className="text-alp-cream font-medium mb-1">Liability Disclaimer</p>
                <p>To the fullest extent permitted by law, Australian Luxe, its owners, representatives, contributors, affiliates and associated parties disclaim all liability for any loss, injury, damage, misuse, adverse outcome, or consequence arising directly or indirectly from the use of, reliance on, or interpretation of any information, product, guide, material, or content provided. By accessing, receiving, purchasing, or using Australian Luxe content or products, you acknowledge and accept these terms.</p>
              </div>
            </div>
          </details>

          {/* Short footer (always visible) */}
          <div className="max-w-3xl mx-auto mt-10 text-center text-alp-gray text-xs leading-relaxed" data-testid="library-short-footer">
            <p>Australian Luxe educational content is for research reference only.</p>
            <p>Research use only. Not for human consumption.</p>
            <p>This information is not medical advice and is not intended to diagnose, treat, cure, or prevent any disease.</p>
            <p>Always seek guidance from a qualified healthcare professional.</p>
            <p>You are responsible for complying with all applicable laws and regulations.</p>
            <p className="mt-3 text-alp-gray/70">Copyright &copy; Australian Luxe, 2026. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {openItem && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 backdrop-blur-sm px-4 py-10"
          data-testid="library-detail-modal"
          onClick={() => setOpenItem(null)}
        >
          <div className="relative w-full max-w-2xl bg-alp-card border border-alp-line rounded-xl my-auto overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpenItem(null)} data-testid="library-detail-close" className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full bg-alp-black/60 border border-alp-line flex items-center justify-center text-alp-gray hover:text-alp-gold hover:border-alp-gold transition-colors">
              <X size={18} />
            </button>
            {(openItem.images || []).length > 0 ? (
              <div className="bg-alp-dark p-4" data-testid="library-sheet-gallery">
                <p className="text-alp-gold text-[10px] tracking-[0.25em] uppercase mb-3">Cheat Sheets &mdash; tap to enlarge</p>
                <div className={`grid gap-3 ${openItem.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {openItem.images.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => { setViewerSrc(src); setViewerZoom(false); }}
                      data-testid={`library-sheet-thumb-${i}`}
                      className="relative group/thumb border border-alp-line rounded-lg overflow-hidden hover:border-alp-gold transition-colors"
                    >
                      <img src={src} alt={`${openItem.title} sheet ${i + 1}`} draggable={false} onContextMenu={(e) => e.preventDefault()} className="w-full h-auto max-h-64 object-cover object-top pointer-events-none select-none" />
                      <span className="absolute inset-0 flex items-center justify-center bg-alp-black/0 group-hover/thumb:bg-alp-black/40 transition-colors">
                        <ZoomIn size={22} className="text-alp-gold opacity-0 group-hover/thumb:opacity-100 transition-opacity" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : openItem.image ? (
              <button onClick={() => { setViewerSrc(openItem.image); setViewerZoom(false); }} className="block w-full" data-testid="library-sheet-single">
                <img src={openItem.image} alt={openItem.title} draggable={false} onContextMenu={(e) => e.preventDefault()} className="w-full max-h-[320px] object-cover pointer-events-none select-none" />
              </button>
            ) : null}
            <div className="p-6 md:p-8">
              <p className="text-alp-gold text-[10px] tracking-[0.25em] uppercase mb-2">{openItem.category || 'General'}</p>
              <h3 className="font-serif text-2xl md:text-3xl text-alp-cream mb-5">{openItem.title}</h3>
              {openItem.whatItIs ? (
                <div className="mb-5">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-1">What it is</p>
                  <p className="text-alp-cream/80 text-sm leading-relaxed whitespace-pre-wrap">{openItem.whatItIs}</p>
                </div>
              ) : null}
              {openItem.use ? (
                <div className="mb-5">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-1">Use</p>
                  <p className="text-alp-cream/80 text-sm leading-relaxed whitespace-pre-wrap">{openItem.use}</p>
                </div>
              ) : null}
              {(openItem.bacWater || openItem.dosage || openItem.timing || openItem.cycle) ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5 border-y border-alp-line/60 py-4">
                  {openItem.bacWater ? <div><p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Recommended BAC Water</p><p className="text-alp-gold text-sm">{openItem.bacWater}</p></div> : null}
                  {openItem.dosage ? <div><p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Dosage</p><p className="text-alp-gold text-sm">{openItem.dosage}</p></div> : null}
                  {openItem.timing ? <div><p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Timing</p><p className="text-alp-gold text-sm">{openItem.timing}</p></div> : null}
                  {openItem.cycle ? <div><p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Cycle</p><p className="text-alp-gold text-sm">{openItem.cycle}</p></div> : null}
                </div>
              ) : null}
              {openItem.body ? <p className="text-alp-cream/80 text-sm leading-relaxed whitespace-pre-wrap">{openItem.body}</p> : null}
              {matchProducts(openItem, products).length > 0 ? (
                <div className="mt-6 border border-alp-gold/30 rounded-lg bg-alp-black/40 p-4" data-testid="library-shop-block">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">Shop this peptide</p>
                  <div className="space-y-2">
                    {matchProducts(openItem, products).map((p) => {
                      const out = p.stockStatus === 'Out of Stock';
                      return (
                        <div key={p.id} className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-3 min-w-0">
                            {p.image ? <img src={p.image} alt={p.name} className="w-10 h-10 rounded object-cover border border-alp-line shrink-0" /> : null}
                            <div className="min-w-0">
                              <p className="text-alp-cream text-sm truncate">{p.name} <span className="text-alp-gray text-xs">{p.size}</span></p>
                              <p className="text-alp-gold text-sm">${p.price.toFixed(2)} <span className="text-alp-gray/70 text-[10px] uppercase">AUD</span></p>
                            </div>
                          </div>
                          <button
                            onClick={() => { addToCart(p, 1); openDrawer(); }}
                            disabled={out}
                            data-testid={`library-add-to-cart-${p.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors ${out ? 'bg-alp-card text-alp-gray border border-alp-line cursor-not-allowed' : 'btn-glow bg-alp-gold text-alp-black hover:bg-alp-goldlight'}`}
                          >
                            <ShoppingBag size={14} /> {out ? 'Out of stock' : 'Add to Cart'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {(openItem.bacWater || openItem.dosage) ? (
                <Link to={calcLinkFor(openItem)} data-testid="library-calc-link" className="btn-glow mt-6 inline-flex items-center gap-2 bg-alp-gold text-alp-black px-6 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">
                  <CalcIcon size={15} /> Open in Calculator
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {/* Fullscreen cheat sheet viewer (tap to zoom) */}
      {viewerSrc && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 flex flex-col"
          data-testid="library-sheet-viewer"
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between px-4 py-3 shrink-0">
            <p className="text-alp-gray text-xs tracking-[0.2em] uppercase">{viewerZoom ? 'Tap image to zoom out' : 'Tap image to zoom in \u00b7 pinch on mobile'}</p>
            <button onClick={() => setViewerSrc('')} data-testid="library-sheet-viewer-close" className="w-10 h-10 rounded-full bg-alp-card/70 border border-alp-line flex items-center justify-center text-alp-cream hover:text-alp-gold hover:border-alp-gold transition-colors">
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 overflow-auto px-2 pb-4" style={{ touchAction: 'pinch-zoom' }}>
            <img
              src={viewerSrc}
              alt="Cheat sheet"
              draggable={false}
              onClick={() => setViewerZoom((z) => !z)}
              className={`select-none mx-auto transition-all duration-200 cursor-zoom-in ${viewerZoom ? 'max-w-none w-[180%] md:w-[130%] cursor-zoom-out' : 'max-w-full w-auto max-h-full object-contain'}`}
            />
          </div>
        </div>
      )}

      {/* Back to top */}
      {scrolled && !viewerSrc && !openItem ? (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          data-testid="library-back-to-top"
          aria-label="Back to top"
          className="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-full bg-alp-gold text-alp-black shadow-[0_4px_20px_rgba(201,162,39,0.4)] flex items-center justify-center hover:bg-alp-goldlight transition-colors"
        >
          <ArrowUp size={18} />
        </button>
      ) : null}
    </div>
  );
}
